import React from 'react'
import PropTypes from 'prop-types'

import { FieldContext } from '../'
import { requiredValidationErrorMessage } from '../../utils'
import FormFieldRegistrationManager from '../../FormFieldRegistrationManager'
import SelectInput, { TwoPartLabel } from '../../../SelectInput'
import TextInput from '../../../TextInput'

class PhoneInput extends React.Component {
  constructor (props) {
    super(props)

    const { defaultValue, defaultCountryCode, countryCodes } = props
    let countryCode = defaultCountryCode || '+234'
    let phoneNumber = ''

    if (defaultValue && defaultValue.trim()) {
      const matchingCountryCode = countryCodes.find(cc =>
        defaultValue.startsWith(cc.value)
      )

      if (matchingCountryCode) {
        countryCode = matchingCountryCode.value
        phoneNumber = defaultValue.substring(countryCode.length)
      } else {
        phoneNumber = defaultValue
      }
    }

    this.state = {
      countryCode,
      phoneNumber
    }
  }

  handleCountryCodeChange = (newCountryCode) => {
    this.setState({ countryCode: newCountryCode }, () => {
      const fullPhoneNumber = `${newCountryCode}${this.state.phoneNumber}`
      if (typeof this.props.onValueChange === 'function') {
        this.props.onValueChange(fullPhoneNumber)
      }
    })
  }

  handlePhoneNumberChange = (newPhoneNumber) => {
    this.setState({ phoneNumber: newPhoneNumber })
    const fullPhoneNumber = `${this.state.countryCode}${newPhoneNumber}`
    if (typeof this.props.onValueChange === 'function') {
      this.props.onValueChange(fullPhoneNumber)
    }
  }

  render () {
    const {
      required,
      validate,
      defaultValue,
      countryCodes,
      placeholder,
      ...otherProps
    } = this.props

    const { countryCode, phoneNumber } = this.state

    return (
      <FieldContext.Consumer>
        {(field) => (
          <FormFieldRegistrationManager
            register={() => {
              field.register({
                required,
                validate,
                requiredValidator: async (value) => {
                  if (!required) return null

                  if (!value || !value.trim()) {
                    return requiredValidationErrorMessage(required)
                  }

                  const matchingCountryCode = countryCodes.find(cc => value.startsWith(cc.value))

                  if (!matchingCountryCode) {
                    return value.trim() ? null : requiredValidationErrorMessage(required)
                  }

                  const phoneNumberPart = value.replace(matchingCountryCode.value, '').trim()

                  if (!phoneNumberPart) {
                    return requiredValidationErrorMessage(required)
                  }

                  return null
                },
                value: `${countryCode}${phoneNumber}`,
                ...otherProps
              })
            }}
            deregister={() => field.deregister()}
          >
            <div className='vs-u-row vs-u-gap vs-u-align-stretch'>
              <SelectInput.Stateless
                value={countryCode}
                options={countryCodes}
                hasError={field.hasErrors()}
                clearable={false}
                onValueChange={(value) => {
                  this.handleCountryCodeChange(value)
                  field.handleOnChange(`${value}${this.state.phoneNumber}`)
                }}
                optionRenderer={(option) => <TwoPartLabel option={option} />}
                valueRenderer={(option) => <TwoPartLabel option={option} />}
                className='vs-form-phone-input__select'
                readOnly={false}
                disabled={false}
              />

              <TextInput
                name={field.props.fieldName}
                className='vs-form-field__input vs-form-phone-input'
                hasError={field.hasErrors()}
                value={phoneNumber}
                onValueChange={(value) => {
                  this.handlePhoneNumberChange(value)
                  field.handleOnChange(`${countryCode}${value}`)
                }}
                placeholder={placeholder || ''}
                type='tel'
                readOnly={false}
                disabled={false}
              />
            </div>
          </FormFieldRegistrationManager>
        )}
      </FieldContext.Consumer>
    )
  }
}

PhoneInput.propTypes = {
  /**
   * Whether the field is required, optionally pass a string to define a custom
   * validation error message
   */
  required: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string
  ]),

  /**
   * A custom validation function
   */
  validate: PropTypes.func,

  /**
   * Runs when field value changes (returns full phone number with country code)
   */
  onValueChange: PropTypes.func,

  /**
   * Initial field value (full phone number including country code)
   */
  defaultValue: PropTypes.string,

  /**
   * Default country code
   */
  defaultCountryCode: PropTypes.string,

  /**
   * Array of country code options
   */
  countryCodes: PropTypes.array,

  /**
   * Placeholder text for the phone number input
   */
  placeholder: PropTypes.string
}

PhoneInput.defaultProps = {
  required: false,
  validate: undefined,
  defaultValue: '',
  defaultCountryCode: '+234',
  countryCodes: [
    { label: '+234', value: '+234' },
    { label: '+254', value: '+254' }
  ],
  placeholder: ''
}

PhoneInput.displayName = 'Form.Field.PhoneInput'

export default PhoneInput
